import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { ChildComponent } from "../../../child-component";
import { NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { IncidentClientModel } from "../../../../../../models/incidents/incident.models";
import { IncidentStatusEnum, SubtaskStatusEnum } from "../../../../../../models/vetting/vetting.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import * as moment from "moment";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../../../configuration/rightshipqi-router.constants";
import { Reflection } from "../../../../../../utilities/reflection";
import { DateFormatValueConverter } from "../../../../../components/converters/date/date-format";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { ProductCodes } from "../../../../../../models/rightstore/rightstore.models";
import { RightStoreService } from "../../../../../../services/rightstore/rightstore.service";

@autoinject
@transient()
@useView('views/pages/modules/incident/tabs/overview/incident-overview.html')
export class IncidentOverview extends ChildComponent<IncidentClientModel>
{
    reassignment: IncidentStatusEnum = IncidentStatusEnum.IncidentReassignment;
    rightShipUser: boolean;
    canShowOverrideHistory: boolean;
    canSubmitCloseout: boolean;
    canViewIncidentEdits: boolean;
    canEditGeneralIncidentDetails: boolean;
    canEditClosedIncidentDetails: boolean;
    canEditIncidentCasualtyOrDemolitionPermission: boolean;
    canViewCategoryWhenGracePeriodActive: boolean;
    changeTab: () => void;

    navigateToInspection(id) {
        window.open('insp/' + id, '_blank');
    }

    constructor(notificationsService: NotificationClientService, i18N: I18N, private userClaimsService: UserClaimsService,
        protected dateFormatValueConverter: DateFormatValueConverter, private router: Router, private rightStoreService:RightStoreService) {
        super(notificationsService, i18N);
        this.rightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
        this.canShowOverrideHistory = userClaimsService.hasPermission(Claims.CanViewIncidentOverrideHistory);
        this.canSubmitCloseout = userClaimsService.hasPermission(Claims.CanSubmitIncidentCloseout);
        this.canViewIncidentEdits = userClaimsService.hasPermission(Claims.CanViewIncidentEdits);
        this.canEditGeneralIncidentDetails = this.userClaimsService.hasPermission(Claims.CanEditIncidentDetails);
        this.canEditClosedIncidentDetails = this.userClaimsService.hasPermission(Claims.CanEditClosedIncidentDetails);
        this.canEditIncidentCasualtyOrDemolitionPermission = this.userClaimsService.hasPermission(Claims.CanEditIncidentCasualtyOrDemolition);
        this.canViewCategoryWhenGracePeriodActive = this.userClaimsService.hasPermission(Claims.CanViewCategoryWhenGracePeriodActive);
    }

    @computedFrom(Reflection.getPropertyPath<IncidentOverview>(z => z.item),
    Reflection.getPropertyPath<IncidentOverview>(z => z.item.closeoutSubtaskId),
    Reflection.getPropertyPath<IncidentOverview>(z => z.item.incidentStatusId))
    get isIncidentCloseoutDisabled(){
        //Close out in progress = If RFI has value && RFI not closed and the Incident Status not closed.
        return (this.item.closeoutSubtaskId && this.item.closeoutSubtaskStatusId != SubtaskStatusEnum.Closed) || this.item.incidentStatusId == IncidentStatusEnum.Closed;
    }

    @computedFrom(Reflection.getPropertyPath<IncidentOverview>(z => z.item),
    Reflection.getPropertyPath<IncidentOverview>(z => z.item.incidentStatusId),
    Reflection.getPropertyPath<IncidentOverview>(z => z.item.remainingGracePeriod))
    get hideSafetyScoreGracePeriod(){
        return this.item.incidentStatusId == IncidentStatusEnum.Closed || this.item.incidentStatusId == IncidentStatusEnum.Misreported || !(this.item.remainingGracePeriod > 0);
    }
    
    async showContactVetSuperDialog() {
        await this.rightStoreService.submitCloseOut(ProductCodes.IncidentCloseOutPriority, ProductCodes.IncidentCloseOutFree, this.item.incidentId, EntityTypes.Incident, this.item.vesselId, null, (() => {
            this.canSubmitCloseout = false;
        }).bind(this, this.canSubmitCloseout));
    }

    getConcatenatedDataSources() {
        return this.item.source;
    }
    
    navigateToVessel() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.item.vesselId });
    }

    @computedFrom("item", "item.incidentStatusId")
    get closed() {
        return this.item &&
            this.item.incidentStatusId == IncidentStatusEnum.Closed;
    }
    
    @computedFrom("item", "item.statuses", "item.incidentStatusId", "item.incidentStatus")
    get closedOverriddenBy() {
        if(this.item && this.item.incidentStatusId == IncidentStatusEnum.Closed){
            const status = this.item.statuses.find(x=>x.name == this.item.incidentStatus);
            if(status) return `overridden by ${status.user}, ${moment(status.date).format("DD MMM YYYY HH:mm")}`
        }
        return '';
    }

    @computedFrom("closed", "canEditGeneralIncidentDetails", "canEditClosedIncidentDetails", "item.isInconsistentState", "canViewIncidentEdits")
    get canViewOrEditIncidentDetails()
    {
        return this.closed ? (this.canEditGeneralIncidentDetails && this.canEditClosedIncidentDetails) || this.canViewIncidentEdits : (this.canEditGeneralIncidentDetails && !this.item.isInconsistentState) || this.canViewIncidentEdits;
    }
    
    @computedFrom("closed", "canEditIncidentCasualtyOrDemolitionPermission", "canEditClosedIncidentDetails", "canViewIncidentEdits")
    get canViewOrEditIncidentCasualtyOrDemolition()
    {
        return this.closed ? (this.canEditIncidentCasualtyOrDemolitionPermission && this.canEditClosedIncidentDetails) || this.canViewIncidentEdits : this.canEditIncidentCasualtyOrDemolitionPermission || this.canViewIncidentEdits;
    }

    @computedFrom("item", "item.incidentStatusId")
    get isMisreported() {
        return this.item &&
            this.item.incidentStatusId == IncidentStatusEnum.Misreported;
    }

    @computedFrom(Reflection.getPropertyPath<IncidentOverview>(x => x.item), Reflection.getPropertyPath<IncidentOverview>(x => x.item.vesselDateOfBuild))
    public get age() {
        if (this.item) {
            if (this.item.vesselDateOfBuild) {
                return (Math.floor(moment().diff(this.item.vesselDateOfBuild, 'year', true) * 10) / 10).toFixed(1) + "y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    public getNullableBoolText(value: boolean): string {
        if (value == true) return 'Yes';
        if (value == false) return 'No';
        if (value == null) return 'N/A'
        return null;
    }

    public getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A'

        let val: any = date;

        if (typeof date == 'string')
            val = new Date(date);

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return this.dateFormatValueConverter.toView(date, 'D MMM YYYY, HH:mm', null);
    }

    public goToOverrideHistory() {
        this.changeTab();
    }

    public remainingGracePeriodDate(remainingGracePeriodDays: number){
        const today = new Date();

        //Add the remaining grace period days to today's date
        today.setDate(today.getDate() + remainingGracePeriodDays);

        const newDate = moment(today).format("DD MMM YYYY");

        return newDate;
    }
}